import React, { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import { formatDate } from "common/calendar";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import { NOTIFICATION_REFERENCE_PARAM } from "common/notification-routes";
import { formatCurrency } from "common/format-currency-pattern";
import AppStatus from "components/app-status";
import AppEmptyState from "components/app-empty-state";
import AppClaimModal from "components/pages/claim-management/app-claim-modal";
import AppTable, { AppTableEditIcon, AppTableCell } from "components/app-table";
import previewIcon from "assets/images/preview-icon.svg";
import pdfIcon from "assets/images/pages/leave-management/pdf-icon.svg";

const PageClaimSummary = (props) => {
	const claimModalRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({
		statusListStr: searchParams.get("statusListStr") || "CANCELLED,APPROVED,REJECTED",
		page: parseInt(searchParams.get("page")) || 1,
		sort: searchParams.get("sort") || "",
	});
	const notificationRefId = useRef(searchParams.get(NOTIFICATION_REFERENCE_PARAM) || "");
	//prettier-ignore
	const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status, [data.status]);
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.claims.history(sanitizeObject(payload));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, []);

	//prettier-ignore
	const onHandlePagination = useCallback((event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	}, [onHandleGetList, data.total]);

	const onHandleViews = useCallback((id) => {
		claimModalRef.current.onHandleShow(id);
	}, []);

	//prettier-ignore
	const onHandleSort = useCallback((id, order) => {
		paramsRef.current.sort = order ? id + "," + order : "";

		paramsRef.current.page = 1;

		onHandleGetList();
	}, [onHandleGetList]);

	const ClaimAmountCell = useCallback(({ row }) => <AppTableCell right value={formatCurrency(row.original.claimValue)} />, []);

	const SubmitDateCell = useCallback(({ row }) => <AppTableCell center value={formatDate(row.original.createdDate)} />, []);

	//prettier-ignore
	const MenuViewCell = useCallback(({ row }) => {
		return <AppTableEditIcon icon={previewIcon} onClick={() => onHandleViews(row.original.id)} />; 
	}, [onHandleViews]);

	//prettier-ignore
	const AttachmentCell = useCallback(({ row }) => {
		const file = row.original?.companyClaimApplicationFiles?.[0];
		const value = file ? (<a href={file?.fileURL} target="_blank" rel="noopener noreferrer"><img className="table__file" src={pdfIcon} alt="attacment" /></a>) : ("-");
		return (
			<AppTableCell center value={value} />
		);
	}, []);

	//prettier-ignore
	const EInvoiceExemptedCell = useCallback(({ row }) => <AppTableCell center value={row.original.eInvoiceExempted ? "Yes" : "No"} />, []);

	const StatusCell = useCallback(({ row }) => {
		return <AppStatus status={row.values.status} />;
	}, []);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Claim Type",
			accessor: "claimTypeMapped",
			disableSortBy: false,
		},
		{
			Header: () => <div className="table__date">Submit Date</div>,
			accessor: "createdDate",
			disableSortBy: false,
			Cell: SubmitDateCell
		},
		{
			Header: () => <div className="table__amount">Amount (RM)</div>,
			accessor: "claimValue",
			disableSortBy: false,
			Cell: ClaimAmountCell,
		},
		{
			Header: () => <div className="table__attachment">Attachment</div>,
			accessor: "companyClaimApplicationFiles",
			disableSortBy: true,
			Cell: AttachmentCell,
		},
		{
			Header: () => <div className="table__e-invoice-exempted">E-Invoice Exempted</div>,
			accessor: "eInvoiceExempted",
			disableSortBy: true,
			Cell: EInvoiceExemptedCell,
		},
        {
			Header: "Status",
			accessor: "status",
			disableSortBy: false,
			Cell: StatusCell,
		},
        {
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuViewCell,
		},
	], [MenuViewCell, StatusCell, AttachmentCell, SubmitDateCell, ClaimAmountCell, EInvoiceExemptedCell]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	// opens the detail modal when arriving from a notification, then strips the
	// param so the same notification can be clicked again.
	useEffect(() => {
		const referenceId = searchParams.get(NOTIFICATION_REFERENCE_PARAM) || notificationRefId.current;

		if (!referenceId) return;

		notificationRefId.current = "";

		claimModalRef.current?.onHandleShow(referenceId);

		memoSetSearchParams.current(sanitizeObject({ ...paramsRef.current, size: 10 }), { replace: true });
	}, [searchParams]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.CLAIMS.HISTORY);
		};
	}, [cancelRequest]);

	return (
		<div className="page-claim-history">
			<div className="claim-history">
				{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}
				{!isEmptyState && <AppTable columns={columns} pages={data} onHandleSort={onHandleSort} onHandlePagination={onHandlePagination} />}
			</div>

			<AppClaimModal ref={claimModalRef} />
		</div>
	);
};

export default PageClaimSummary;
